import React, { useEffect, useMemo, useState } from "react";
import { computeFEFOSplit, applyFEFOSplit, makeLineId } from "@/lib/fefoSplit";
import { AutoSplitFEFODialog } from "@/components/AutoSplitFEFODialog";
import { DireccionEntregaSelect } from "@/components/ClienteContactos";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, FileText, AlertTriangle, ArrowLeft, Save, Lock, Unlock, Users as UsersIcon, RefreshCw, Layers } from "lucide-react";
import { ProductoCombobox } from "@/components/ProductoCombobox";
import { ClienteCombobox } from "@/components/ClienteCombobox";
import { PriceCombobox } from "@/components/PriceCombobox";
import { NumericInput } from "@/components/NumericInput";
import PinGerenciaModal from "@/components/PinGerenciaModal";
import { Badge } from "@/components/ui/badge";
import { ConsolidateOrdersDialog } from "@/components/ConsolidateOrdersDialog";
import { useAuth } from "@/contexts/AuthContext";

function emptyForm() {
  return {
    cliente_id: "",
    fecha: new Date().toISOString().slice(0, 10),
    forma_pago_sri: "20",
    observaciones: "",
    direccion_entrega: "", direccion_entrega_etiqueta: "",
    informacion_adicional: [],
    dias_credito: 0,
    lineas: [emptyLine()],
  };
}

function emptyLine() {
  return {
    producto_comercial_id: "", codigo_interno: "", producto_nombre: "",
    cantidad: 1, precio_unitario: 0, descuento: 0, subtotal: 0, iva_porc: 0,
    marca: "", laboratorio: "", presentacion: "", registro_sanitario: "",
    lote_id: null, numero_lote: "", fecha_elaboracion: "", fecha_vencimiento: "",
    observacion: "", info_adicional: {},
    _lotes_disponibles: [], _unidad_venta: "caja", _unidades_por_caja: 1,
    _precio_unidad: 0, _precio_caja: 0, _costo_unidad: 0, _margen_fuente: "default",
  };
}

export default function InvoiceForm() {
  const { canSeeCosts } = useAuth();
  const verCostos = canSeeCosts();
  const { id: editingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pedidoIdFromUrl = searchParams.get("pedido");
  const duplicarIdFromUrl = searchParams.get("duplicar");
  const [clientes, setClientes] = useState([]);
  const [prods, setProds] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [expandedLines, setExpandedLines] = useState({});
  const [pinModal, setPinModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Reserva de secuencial (concurrencia entre facturadores)
  const [seqStatus, setSeqStatus] = useState(null); // {siguiente_numero, reservas:[...]}
  const [lock, setLock] = useState(null); // {lock_id, numero_formateado, expires_at}
  const [lockBusy, setLockBusy] = useState(false);
  const [consolidateOpen, setConsolidateOpen] = useState(false);
  // ITER184: ids de NPs consolidadas en esta factura
  const [notasPedidoIds, setNotasPedidoIds] = useState([]);
  // Precios divergentes tras consolidar (advertencia UI)
  const [precioDivergenteAviso, setPrecioDivergenteAviso] = useState(false);

  // Polling del estado del secuencial: refresca cada 15s para mostrar
  // facturas en edición por otros usuarios.
  useEffect(() => {
    if (editingId) return undefined; // En edición no aplica
    let alive = true;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/invoicing/secuencial-status/factura_electronica");
        if (alive) setSeqStatus(data);
      } catch (_) { /* silent */ }
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, [editingId]);

  // Liberar la reserva si el usuario abandona el formulario sin guardar
  useEffect(() => {
    if (!lock) return undefined;
    const handleUnload = () => {
      // beacon: best-effort release (sin garantizar entrega)
      try {
        const url = `/api/invoicing/reservar-secuencial/${lock.lock_id}`;
        navigator.sendBeacon && navigator.sendBeacon(url, new Blob([""], { type: "text/plain" }));
      } catch (_) { /* ignore */ }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      // Si todavía existe lock al desmontar (cancel), liberarlo
      if (lock) {
        api.delete(`/invoicing/reservar-secuencial/${lock.lock_id}`).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lock?.lock_id]);

  const reservarSecuencial = async () => {
    setLockBusy(true);
    try {
      const { data } = await api.post("/invoicing/reservar-secuencial", { tipo: "factura_electronica" });
      setLock(data);
      // ITER187 (Feb 2026): si el backend RECUPERÓ un número liberado
      // previamente por cancelación, mostrar mensaje distintivo para que
      // el usuario sepa que se está reutilizando (y evitando un hueco).
      if (data.recuperado) {
        toast.success(
          `Número ${data.numero_formateado} REUTILIZADO (recuperado de una cancelación previa · evita hueco en la secuencia).`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Número ${data.numero_formateado} reservado para ti (30 min).`);
      }
      try { const r = await api.get("/invoicing/secuencial-status/factura_electronica"); setSeqStatus(r.data); } catch (_) { /* */ }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLockBusy(false); }
  };

  const liberarSecuencial = async () => {
    if (!lock) return;
    setLockBusy(true);
    try {
      await api.delete(`/invoicing/reservar-secuencial/${lock.lock_id}`);
      toast.success(`Número ${lock.numero_formateado} liberado`);
      setLock(null);
      try { const r = await api.get("/invoicing/secuencial-status/factura_electronica"); setSeqStatus(r.data); } catch (_) { /* */ }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLockBusy(false); }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get("/masters/clientes"),
          api.get("/masters/productos-comerciales"),
        ]);
        if (!mounted) return;
        setClientes(c.data.filter(x => x.estado === "aprobado"));
        setProds(p.data);

        if (editingId) {
          const { data } = await api.get(`/invoicing/facturas/${editingId}`);
          if (!["borrador", "firmado", "rechazado"].includes(data.estado_sri)) {
            toast.error(`No se puede editar una factura en estado '${data.estado_sri}'`);
            navigate("/facturacion", { replace: true });
            return;
          }
          // Para cada línea, cargar info-producto (lotes disponibles + precios)
          // para que el usuario pueda cambiar lote, ver presentación y que el
          // toggle U/C muestre/respete el modo persistido.
          const lineasConInfo = await Promise.all(
            (data.lineas || []).map(async (ln) => {
              const base = { ...emptyLine(), ...ln };
              if (!ln.producto_comercial_id) return base;
              try {
                const { data: info } = await api.get(`/invoicing/info-producto/${ln.producto_comercial_id}?cliente_id=${data.cliente_id || ""}`);
                const lotes = info?.lotes_disponibles || [];
                const upc = ln.unidades_por_caja || info?.unidades_por_presentacion || 1;
                return {
                  ...base,
                  _lotes_disponibles: lotes,
                  _unidad_venta: ln.unidad_venta || "unidad",
                  _unidades_por_caja: upc,
                  _precio_unidad: info?.precio_unidad || 0,
                  _precio_caja: info?.precio_caja || (info?.precio_unidad || 0) * upc,
                  _pvp_unidad: info?.pvp || 0,
                  _pvp_caja: info?.pvp_caja || (info?.pvp || 0) * upc,
                  _precio_minimo_unidad: info?.precio_minimo || 0,
                  _precio_minimo_caja: info?.precio_minimo_caja || 0,
                  _precio_techo: info?.precio_techo || null,
                  _costo_unidad: info?.costo_unidad || 0,
                  _margen_aplicado: info?.margen_aplicado || 0,
                  _margen_fuente: info?.margen_fuente || null,
                  presentacion: ln.presentacion || info?.presentacion || "",
                  laboratorio_nombre: ln.laboratorio_nombre || ln.laboratorio || info?.laboratorio || "",
                  laboratorio: ln.laboratorio || info?.laboratorio || "",
                  registro_sanitario: ln.registro_sanitario || info?.registro_sanitario || "",
                  principio_activo: ln.principio_activo || info?.principio_activo || "",
                  concentracion: ln.concentracion || info?.concentracion || "",
                };
              } catch (_) {
                return {
                  ...base,
                  _unidad_venta: ln.unidad_venta || "unidad",
                  _unidades_por_caja: ln.unidades_por_caja || 1,
                  _lotes_disponibles: [],
                };
              }
            })
          );
          setForm({
            cliente_id: data.cliente_id,
            fecha: data.fecha || new Date().toISOString().slice(0, 10),
            forma_pago_sri: data.forma_pago_sri || "20",
            observaciones: data.observaciones || "",
            direccion_entrega: data.direccion_entrega || "",
            direccion_entrega_etiqueta: data.direccion_entrega_etiqueta || "",
            informacion_adicional: data.informacion_adicional || [],
            nota_pedido_id: data.nota_pedido_id || null,
            numero: data.numero || "",
            estado_sri: data.estado_sri || "",
            dias_credito: data.dias_credito ?? 0,
            subtotal: data.subtotal || 0,
            iva: data.iva || 0,
            total: data.total || 0,
            lineas: lineasConInfo,
          });
        } else if (pedidoIdFromUrl) {
          // === Pre-carga desde Nota de Pedido ===
          // Trae cliente + líneas del pedido, y para cada línea consulta
          // /invoicing/info-producto/{id} para sugerir lote FEFO automáticamente.
          try {
            const { data: pedido } = await api.get(`/sales/pedidos/${pedidoIdFromUrl}`);
            if (pedido?.estado === "facturado") {
              toast.warning(`Este pedido ${pedido.numero || ""} ya fue facturado previamente.`);
            }
            const lineasConFefo = await Promise.all(
              (pedido.lineas || []).map(async (l) => {
                const base = { ...emptyLine(), ...l };
                if (!l.producto_comercial_id) return base;
                try {
                  const { data: info } = await api.get(`/invoicing/info-producto/${l.producto_comercial_id}?cliente_id=${pedido.cliente_id || ""}`);
                  const lotes = info?.lotes_disponibles || [];
                  const fefo = info?.lote_sugerido || lotes[0]; // FEFO sugerido por el backend
                  const modoPedido = l.unidad_venta || "unidad";
                  const upcPedido = l.unidades_por_caja || info?.unidades_por_presentacion || 1;
                  const isCaja = modoPedido === "caja" && upcPedido > 1;
                  return {
                    ...base,
                    _lotes_disponibles: lotes,
                    _unidad_venta: modoPedido,
                    _unidades_por_caja: upcPedido,
                    _precio_unidad: info?.precio_unidad || 0,
                    _precio_caja: info?.precio_caja || (info?.precio_unidad || 0) * upcPedido,
                    _pvp_unidad: info?.pvp || 0,
                    _pvp_caja: info?.pvp_caja || (info?.pvp || 0) * upcPedido,
                    _precio_minimo_unidad: info?.precio_minimo || 0,
                    _precio_minimo_caja: info?.precio_minimo_caja || 0,
                    _precio_techo: info?.precio_techo || null,
                    _costo_unidad: info?.costo_unidad || 0,
                    _margen_aplicado: info?.margen_aplicado || 0,
                    _margen_fuente: info?.margen_fuente || null,
                    _stock_total: typeof info?.stock_total === "number" ? info.stock_total : undefined,
                    // Conservar el precio que se acordó en el pedido (no recalcular)
                    precio_unitario: l.precio_unitario != null ? l.precio_unitario
                      : (isCaja ? (info?.precio_caja || 0) : (info?.precio_unidad || 0)),
                    iva_porc: l.iva_porc != null ? l.iva_porc : (info?.iva_porcentaje ?? 0),
                    laboratorio_nombre: l.laboratorio_nombre || l.laboratorio || info?.laboratorio || "",
                    laboratorio: l.laboratorio || info?.laboratorio || "",
                    presentacion: l.presentacion || info?.presentacion || "",
                    registro_sanitario: l.registro_sanitario || info?.registro_sanitario || "",
                    principio_activo: info?.principio_activo || "",
                    concentracion: info?.concentracion || "",
                    forma_farmaceutica: info?.forma_farmaceutica || "",
                    lote_id: l.lote_id || fefo?.id || null,
                    numero_lote: l.numero_lote || fefo?.numero_lote || "",
                    fecha_elaboracion: l.fecha_elaboracion || fefo?.fecha_elaboracion || "",
                    fecha_vencimiento: l.fecha_vencimiento || fefo?.fecha_vencimiento || "",
                  };
                } catch (_) {
                  return base;
                }
              })
            );
            // Fallback de días de crédito: pedido → cliente → 0
            let diasCredFallback = pedido.dias_credito;
            if (diasCredFallback == null) {
              try {
                const { data: cli } = await api.get(`/masters/clientes/${pedido.cliente_id}`);
                diasCredFallback = cli?.dias_credito != null ? Number(cli.dias_credito) : 0;
              } catch (_) { diasCredFallback = 0; }
            }
            setForm((f) => ({
              ...f,
              cliente_id: pedido.cliente_id,
              observaciones: pedido.observaciones || "",
              dias_credito: diasCredFallback,
              forma_pago_sri: pedido.forma_pago_sri || f.forma_pago_sri || "20",
              nota_pedido_id: pedido.id,
              lineas: lineasConFefo.length ? lineasConFefo : f.lineas,
            }));
            setNotasPedidoIds([pedido.id]);
            toast.success(`Pedido ${pedido.numero || ""} cargado · ${lineasConFefo.length} línea(s) con lote FEFO sugerido`);
          } catch (e) {
            toast.error(`No se pudo cargar el pedido: ${formatApiError(e)}`);
          }
        } else if (duplicarIdFromUrl) {
          // Carga la factura origen y refresca lotes FEFO + precios via info-producto
          try {
            const { data: dupData } = await api.get(`/invoicing/facturas/${duplicarIdFromUrl}/duplicar`);
            const lineasConFefo = await Promise.all(
              (dupData.lineas || []).map(async (l) => {
                const base = { ...emptyLine(), ...l };
                // Normalizar campos requeridos del schema FacturaLinea (compatible
                // con facturas legacy que tienen `descripcion` o `total` pero no
                // `producto_nombre`/`subtotal`).
                const subtotalNorm = (l.subtotal != null) ? l.subtotal
                  : (l.total != null ? l.total : ((l.cantidad || 0) * (l.precio_unitario || 0)));
                if (!l.producto_comercial_id) return { ...base, subtotal: subtotalNorm, producto_nombre: l.producto_nombre || l.descripcion || base.producto_nombre || "" };
                try {
                  const { data: info } = await api.get(`/invoicing/info-producto/${l.producto_comercial_id}?cliente_id=${dupData.cliente_id || ""}`);
                  const lotes = info?.lotes_disponibles || [];
                  const fefo = info?.lote_sugerido || lotes[0];
                  const modo = l.unidad_venta || "unidad";
                  const upc = l.unidades_por_caja || info?.unidades_por_presentacion || 1;
                  const isCaja = modo === "caja" && upc > 1;
                  const producto_nombre = l.producto_nombre || l.descripcion || info?.nombre_comercial || base.producto_nombre || "";
                  const codigo_interno = l.codigo_interno || l.codigo_principal || info?.codigo_interno || "";
                  return {
                    ...base,
                    producto_comercial_id: l.producto_comercial_id,
                    producto_nombre,
                    codigo_interno,
                    subtotal: subtotalNorm,
                    _lotes_disponibles: lotes,
                    _unidad_venta: modo,
                    _unidades_por_caja: upc,
                    _precio_unidad: info?.precio_unidad || 0,
                    _precio_caja: info?.precio_caja || (info?.precio_unidad || 0) * upc,
                    _pvp_unidad: info?.pvp || 0,
                    _pvp_caja: info?.pvp_caja || (info?.pvp || 0) * upc,
                    _precio_minimo_unidad: info?.precio_minimo || 0,
                    _precio_minimo_caja: info?.precio_minimo_caja || 0,
                    _precio_techo: info?.precio_techo || null,
                    _costo_unidad: info?.costo_unidad || 0,
                    _margen_aplicado: info?.margen_aplicado || 0,
                    _stock_total: typeof info?.stock_total === "number" ? info.stock_total : undefined,
                    precio_unitario: l.precio_unitario != null ? l.precio_unitario
                      : (isCaja ? (info?.precio_caja || 0) : (info?.precio_unidad || 0)),
                    iva_porc: l.iva_porc != null ? l.iva_porc : (info?.iva_porcentaje ?? 0),
                    laboratorio_nombre: l.laboratorio_nombre || l.laboratorio || info?.laboratorio || "",
                    laboratorio: l.laboratorio || info?.laboratorio || "",
                    presentacion: l.presentacion || info?.presentacion || "",
                    registro_sanitario: l.registro_sanitario || info?.registro_sanitario || "",
                    principio_activo: info?.principio_activo || "",
                    concentracion: info?.concentracion || "",
                    forma_farmaceutica: info?.forma_farmaceutica || "",
                    lote_id: l.lote_id || fefo?.id || null,
                    numero_lote: l.numero_lote || fefo?.numero_lote || "",
                    fecha_elaboracion: l.fecha_elaboracion || fefo?.fecha_elaboracion || "",
                    fecha_vencimiento: l.fecha_vencimiento || fefo?.fecha_vencimiento || "",
                  };
                } catch (_) {
                  return { ...base, subtotal: subtotalNorm };
                }
              })
            );
            setForm((f) => ({
              ...f,
              cliente_id: dupData.cliente_id,
              forma_pago_sri: dupData.forma_pago_sri || "20",
              dias_credito: dupData.dias_credito ?? 0,
              observaciones: dupData.observaciones || "",
              informacion_adicional: dupData.informacion_adicional || [],
              lineas: lineasConFefo.length ? lineasConFefo : f.lineas,
            }));
            toast.success(`Factura ${dupData._duplicado_de?.numero || ""} duplicada. Revise lotes y precios antes de guardar.`);
          } catch (e) {
            toast.error(`No se pudo duplicar la factura: ${formatApiError(e)}`);
          }
        }
      } catch (e) {
        toast.error(formatApiError(e));
        if (editingId) navigate("/facturacion", { replace: true });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [editingId, navigate, pedidoIdFromUrl, duplicarIdFromUrl]);

  const addLinea = () => setForm(f => ({ ...f, lineas: [...f.lineas, emptyLine()] }));
  const toggleExpand = (i) => setExpandedLines(e => ({ ...e, [i]: !e[i] }));

  const toggleUnidadVenta = (i, modo) => setForm(f => {
    const nl = [...f.lineas];
    nl[i] = {
      ...nl[i],
      _unidad_venta: modo,
      unidad_venta: modo,
      precio_unitario: modo === "caja" ? (nl[i]._precio_caja || 0) : (nl[i]._precio_unidad || 0),
    };
    nl[i].subtotal = Math.max(0, (parseFloat(nl[i].cantidad) || 0) * (parseFloat(nl[i].precio_unitario) || 0) - (parseFloat(nl[i].descuento) || 0));
    // ITER182: al cambiar U/C re-evaluar split (métrica de disponibilidad cambia)
    setTimeout(() => detectFEFOSplit(i, nl[i]), 100);
    return { ...f, lineas: nl };
  });

  const updateLinea = (i, field, val) => setForm(f => {
    const nl = [...f.lineas]; nl[i] = { ...nl[i], [field]: val };
    const cantidad = parseFloat(nl[i].cantidad) || 0;
    const precio = parseFloat(nl[i].precio_unitario) || 0;
    const bruto = cantidad * precio;
    const descPct = Math.min(100, Math.max(0, parseFloat(nl[i].descuento_pct) || 0));
    nl[i].descuento_pct = descPct;
    nl[i].descuento = +(bruto * descPct / 100).toFixed(4);
    nl[i].subtotal = +Math.max(0, bruto - nl[i].descuento).toFixed(2);
    // ITER170: detectar necesidad de split FEFO
    if (field === "cantidad" || field === "unidad_venta") setTimeout(() => detectFEFOSplit(i, nl[i]), 250);
    return { ...f, lineas: nl };
  });

  // ITER170: auto-split FEFO helper para facturas
  const [splitProposal, setSplitProposal] = useState(null);
  const recalcLineForSplit = (l) => {
    const cantidad = parseFloat(l.cantidad) || 0;
    const precio = parseFloat(l.precio_unitario) || 0;
    const bruto = cantidad * precio;
    const descPct = Math.min(100, Math.max(0, parseFloat(l.descuento_pct) || 0));
    const desc = +(bruto * descPct / 100).toFixed(4);
    return { ...l, descuento_pct: descPct, descuento: desc, subtotal: +Math.max(0, bruto - desc).toFixed(2) };
  };
  const detectFEFOSplit = (idx, updatedLine) => {
    // ITER182: siempre proponer split si hay plan
    if (!updatedLine.lote_id) return;
    const cant = Number(updatedLine.cantidad) || 0;
    const split = computeFEFOSplit(updatedLine, cant);
    if (split && split.plan.length >= 1) {
      setSplitProposal({ idx, split, producto_nombre: updatedLine.producto_nombre });
    }
  };
  const applyProposedSplit = () => {
    if (!splitProposal) return;
    setForm(f => ({
      ...f,
      lineas: applyFEFOSplit(f.lineas, splitProposal.idx, splitProposal.split, recalcLineForSplit, makeLineId),
    }));
    setSplitProposal(null);
  };

  const onLoteChange = (i, loteId) => setForm(f => {
    const nl = [...f.lineas];
    const lotes = nl[i]._lotes_disponibles || [];
    const lote = lotes.find(x => x.id === loteId);
    if (lote) {
      nl[i] = {
        ...nl[i],
        lote_id: lote.id,
        numero_lote: lote.numero_lote || "",
        fecha_elaboracion: lote.fecha_elaboracion || "",
        fecha_vencimiento: lote.fecha_vencimiento || "",
        // ITER179: alerta corta fecha al cambiar lote
        _vencimiento_corto: !!lote.vencimiento_corto,
        _dias_hasta_vencimiento: lote.dias_hasta_vencimiento ?? null,
      };
    }
    return { ...f, lineas: nl };
  });

  const onProductoChange = async (i, productoId) => {
    if (!productoId) return;
    try {
      const params = form.cliente_id ? `?cliente_id=${form.cliente_id}` : "";
      const { data } = await api.get(`/invoicing/info-producto/${productoId}${params}`);
      setForm(f => {
        const nl = [...f.lineas];
        const unidadDefault = data.permite_venta_presentacion !== false && data.unidades_por_presentacion > 1 ? "caja" : "unidad";
        const precioDefault = unidadDefault === "caja" ? (data.precio_caja || 0) : (data.precio_unidad || 0);
        nl[i] = {
          ...nl[i],
          producto_comercial_id: productoId,
          codigo_interno: data.codigo_interno || "",
          producto_nombre: data.nombre_comercial || "",
          precio_unitario: precioDefault,
          iva_porc: data.iva_exento ? -1 : (data.iva_porcentaje ?? 0),
          marca: data.marca || "",
          laboratorio: data.laboratorio || "",
          presentacion: data.presentacion || "",
          registro_sanitario: data.registro_sanitario || "",
          lote_id: data.lote_sugerido?.id || null,
          numero_lote: data.lote_sugerido?.numero_lote || "",
          fecha_elaboracion: data.lote_sugerido?.fecha_elaboracion || "",
          fecha_vencimiento: data.lote_sugerido?.fecha_vencimiento || "",
          _lotes_disponibles: data.lotes_disponibles || [],
          _unidad_venta: unidadDefault,
          _unidades_por_caja: data.unidades_por_presentacion || 1,
          _precio_unidad: data.precio_unidad || 0,
          _precio_caja: data.precio_caja || 0,
          _pvp_unidad: data.pvp || 0,
          _pvp_caja: data.pvp_caja || 0,
          _precio_minimo_unidad: data.precio_minimo || 0,
          _precio_minimo_caja: data.precio_minimo_caja || 0,
          _costo_unidad: data.costo_unidad || 0,
          _margen_aplicado: data.margen_aplicado || 0,
          _margen_fuente: data.margen_fuente || "default",
          // ITER179: corta fecha
          _vencimiento_corto: !!(data.lote_sugerido?.vencimiento_corto),
          _dias_hasta_vencimiento: data.lote_sugerido?.dias_hasta_vencimiento ?? null,
          _dias_alerta_venc_corto: data.dias_alerta_vencimiento_corto ?? 180,
        };
        nl[i].subtotal = Math.max(0, (parseFloat(nl[i].cantidad) || 0) * (parseFloat(nl[i].precio_unitario) || 0) - (parseFloat(nl[i].descuento) || 0));
        return { ...f, lineas: nl };
      });
      setExpandedLines(e => ({ ...e, [i]: true }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ITER184 (Feb 2026): consolidar múltiples NP en una factura
  const applyConsolidation = async (data) => {
    if (!data?.lineas?.length) {
      toast.warning("La consolidación no devolvió líneas");
      return;
    }
    // Para cada línea consolidada, hidratar con info-producto (lotes, precios, márgenes)
    // similar al patrón usado al cargar desde pedido único.
    const lineasHidratadas = await Promise.all(
      data.lineas.map(async (l) => {
        const base = { ...emptyLine(), ...l };
        if (!l.producto_comercial_id) return base;
        try {
          const { data: info } = await api.get(`/invoicing/info-producto/${l.producto_comercial_id}?cliente_id=${data.cliente_id || ""}`);
          const lotes = info?.lotes_disponibles || [];
          const upc = l.unidades_por_caja || info?.unidades_por_presentacion || 1;
          return {
            ...base,
            _lotes_disponibles: lotes,
            _unidad_venta: l.unidad_venta || "unidad",
            _unidades_por_caja: upc,
            _precio_unidad: info?.precio_unidad || 0,
            _precio_caja: info?.precio_caja || (info?.precio_unidad || 0) * upc,
            _pvp_unidad: info?.pvp || 0,
            _pvp_caja: info?.pvp_caja || (info?.pvp || 0) * upc,
            _precio_minimo_unidad: info?.precio_minimo || 0,
            _precio_minimo_caja: info?.precio_minimo_caja || 0,
            _precio_techo: info?.precio_techo || null,
            _costo_unidad: info?.costo_unidad || 0,
            _margen_aplicado: info?.margen_aplicado || 0,
            _margen_fuente: info?.margen_fuente || null,
            _precios_inconsistentes: !!info?.precios_inconsistentes,
            laboratorio_nombre: l.laboratorio_nombre || l.laboratorio || info?.laboratorio || "",
            laboratorio: l.laboratorio || info?.laboratorio || "",
            presentacion: l.presentacion || info?.presentacion || "",
            registro_sanitario: l.registro_sanitario || info?.registro_sanitario || "",
            principio_activo: info?.principio_activo || "",
            concentracion: info?.concentracion || "",
          };
        } catch (_) {
          return base;
        }
      })
    );
    setForm(f => ({
      ...f,
      cliente_id: data.cliente_id,
      dias_credito: data.dias_credito ?? f.dias_credito ?? 0,
      forma_pago_sri: data.forma_pago_sri || f.forma_pago_sri || "20",
      nota_pedido_id: (data.notas_pedido_ids || [])[0] || f.nota_pedido_id,
      observaciones: data.observaciones_concatenadas || f.observaciones,
      lineas: lineasHidratadas,
    }));
    setNotasPedidoIds(data.notas_pedido_ids || []);
    setPrecioDivergenteAviso(!!data.algun_precio_divergente);
  };

  const totales = useMemo(() => {
    const subtotal = form.lineas.reduce((s, l) => s + (l.subtotal || 0), 0);
    const iva = form.lineas.reduce((s, l) => s + (l.subtotal || 0) * (Math.max(l.iva_porc || 0, 0) / 100), 0);
    return { subtotal: +subtotal.toFixed(2), iva: +iva.toFixed(2), total: +(subtotal + iva).toFixed(2) };
  }, [form.lineas]);

  const resumenCaja = useMemo(() => {
    let lineasCaja = 0, totalCajas = 0, totalUnidEquiv = 0, totalUnidades = 0;
    form.lineas.forEach((l) => {
      const cant = +l.cantidad || 0;
      const upc = +l._unidades_por_caja || 1;
      if (l._unidad_venta === "caja" && upc > 1) {
        lineasCaja += 1;
        totalCajas += cant;
        totalUnidEquiv += cant * upc;
      } else {
        totalUnidades += cant;
      }
    });
    return { lineasCaja, totalCajas, totalUnidEquiv, totalUnidades };
  }, [form.lineas]);

  // ITER179: detectar lineas con corta fecha
  const cortaFechaLineas = useMemo(() => (
    form.lineas.filter(l => l._vencimiento_corto)
  ), [form.lineas]);
  const diasAlertaVenc = form.lineas.find(l => l._dias_alerta_venc_corto)?._dias_alerta_venc_corto ?? 180;

  // ITER223: varias líneas del mismo lote que en total superan su stock
  const lotesSobrepasados = useMemo(() => {
    const porLote = {};
    form.lineas.forEach((l, i) => {
      if (!l.lote_id) return;
      const upc = l._unidad_venta === "caja" && l._unidades_por_caja > 1 ? l._unidades_por_caja : 1;
      const u = (Number(l.cantidad) || 0) * upc;
      if (u <= 0) return;
      const lt = (l._lotes_disponibles || []).find(x => x.id === l.lote_id);
      const e = porLote[l.lote_id] || {
        numero_lote: l.numero_lote || lt?.numero_lote || "—",
        producto: l.producto_nombre,
        disponible: lt ? Number(lt.cantidad_disponible_bruta ?? lt.cantidad_disponible ?? 0) : null,
        unidades: 0, lineas: [],
      };
      e.unidades += u;
      e.lineas.push(i + 1);
      porLote[l.lote_id] = e;
    });
    return Object.values(porLote).filter(e => e.disponible !== null && e.unidades > e.disponible + 0.0001);
  }, [form.lineas]);

  const save = async (autorizadorPin = null) => {
    if (!form.cliente_id) return toast.error("Seleccione cliente aprobado");
    if (form.lineas.length === 0) return toast.error("Agregue productos");
    const violaciones = [];
    form.lineas.forEach((l) => {
      const isCaja = l._unidad_venta === "caja" && l._unidades_por_caja > 1;
      const piso = isCaja ? l._precio_minimo_caja : l._precio_minimo_unidad;
      if (piso && +l.precio_unitario > 0 && +l.precio_unitario + 1e-6 < +piso) {
        violaciones.push({
          producto: l.producto_nombre || l.codigo_interno || l.producto_comercial_id,
          precio: +l.precio_unitario,
          precio_minimo: +piso,
        });
      }
    });
    if (violaciones.length > 0 && !autorizadorPin) {
      setPinModal({ violaciones });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        ...totales,
        // ITER184 (Feb 2026): si consolidamos múltiples NP, enviamos la lista
        notas_pedido_ids: notasPedidoIds.length > 0 ? notasPedidoIds : undefined,
        informacion_adicional: (form.informacion_adicional || [])
          .map(i => ({ label: (i.label || "").trim(), valor: (i.valor || "").trim() }))
          .filter(i => i.label && i.valor),
        lineas: form.lineas.map(({ _lotes_disponibles, _unidad_venta, _unidades_por_caja, ...rest }) => ({
          ...rest,
          unidad_venta: _unidad_venta || "unidad",
          unidades_por_caja: _unidades_por_caja || null,
        })),
      };
      if (autorizadorPin) payload.autorizador_pin_id = autorizadorPin.autorizador_id;
      if (lock?.lock_id) payload.lock_id = lock.lock_id;
      if (editingId) {
        await api.put(`/invoicing/facturas/${editingId}`, payload);
        toast.success("Factura editada · estado: borrador (firma anterior descartada si existía)");
      } else {
        const { data: facturaCreada } = await api.post("/invoicing/facturas", payload);
        // El lock se consume en el backend, lo limpiamos aquí
        setLock(null);
        toast.success(`Factura ${facturaCreada?.numero || ""} generada (borrador) - XML listo para firmar`);
      }
      setPinModal(null);
      navigate("/facturacion");
    } catch (e) {
      const err = formatApiError(e);
      // ITER182 (Feb 2026): auto-recuperación en error de reserva/lote insuficiente
      const detail = e?.response?.data?.detail || err || "";
      const isLoteError = /Lote\s+\S+.*(?:ya\s+está\s+reservado|no\s+tiene\s+stock\s+suficiente)/i.test(detail);
      if (isLoteError) {
        const m = detail.match(/Lote\s+(\S+)/i);
        const loteNum = m ? m[1] : null;
        if (loteNum) {
          const idx = form.lineas.findIndex(l => (l.numero_lote || "") === loteNum);
          if (idx >= 0) {
            try {
              const pcid = form.lineas[idx].producto_comercial_id;
              const { data: info } = await api.get(`/invoicing/info-producto/${pcid}`);
              setForm(f => {
                const nl = [...f.lineas];
                nl[idx] = { ...nl[idx], _lotes_disponibles: info.lotes_disponibles || [] };
                setTimeout(() => detectFEFOSplit(idx, nl[idx]), 150);
                return { ...f, lineas: nl };
              });
              toast.warning("Este lote ya no tiene stock suficiente. Te propuse distribuir en otros lotes disponibles.");
              return;
            } catch (_) { /* fall-through */ }
          }
        }
      }
      // Detectar conflictos de número de factura para mostrar mensaje claro
      if (err && /NUMERO_DUPLICADO|reserva.*expir|secuencial/i.test(err)) {
        toast.error(err);
        // Limpiar lock y refrescar status para que el usuario pueda re-reservar
        if (lock) setLock(null);
        try {
          const r = await api.get("/invoicing/secuencial-status/factura_electronica");
          setSeqStatus(r.data);
        } catch (_) { /* */ }
      } else {
        toast.error(err);
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelar = () => navigate("/facturacion");

  if (loading) return <div className="p-10 text-center text-[#5c7368]">Cargando…</div>;

  return (
    <div className="space-y-4 pb-40" data-testid="invoice-form-page">
      {/* ITER223: aviso de líneas que comparten lote y superan su stock */}
      {lotesSobrepasados.length > 0 && (
        <div className="rounded-md border border-[#d99584] bg-[#fdecea] px-3 py-2 text-sm text-[#7a2514]"
          data-testid="invoice-lote-sobrepasado-warn">
          <b>Revise las cantidades:</b> hay líneas que piden más unidades de las que tiene el lote.
          <ul className="mt-1 space-y-0.5 text-[12px]">
            {lotesSobrepasados.map((e, i) => (
              <li key={i}>
                {e.producto} · lote {e.numero_lote}: {e.lineas.length > 1
                  ? `${e.lineas.length} líneas (${e.lineas.join(", ")}) suman`
                  : "se piden"} {e.unidades} u. y hay {e.disponible} u.
              </li>
            ))}
          </ul>
          <div className="mt-1 text-[12px]">Una sola línea por lote evita confusiones: sume las cantidades o elija otro lote.</div>
        </div>
      )}

      {/* ITER179: Banner corta fecha */}
      {cortaFechaLineas.length > 0 && (
        <div className="mx-4 md:mx-6 mt-2 border-l-4 border-[#c67c1e] bg-[#fdf3e0] p-3 rounded-r-md flex items-start gap-3" data-testid="alerta-corta-fecha-factura">
          <AlertTriangle className="w-5 h-5 text-[#c67c1e] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#7a4b0b]">
            <b>Atención:</b> Esta factura incluye {cortaFechaLineas.length} producto(s) con vencimiento menor a {diasAlertaVenc} días. Confirme con el cliente antes de facturar.
            <ul className="mt-1 space-y-0.5">
              {cortaFechaLineas.slice(0, 5).map((l, i) => (
                <li key={i} className="text-xs">
                  · {l.producto_nombre} · Lote {l.numero_lote || "—"} · vence {l.fecha_vencimiento || "—"}
                  {l._dias_hasta_vencimiento != null && <span className="ml-1 font-semibold">({l._dias_hasta_vencimiento} días)</span>}
                </li>
              ))}
              {cortaFechaLineas.length > 5 && <li className="text-xs">... y {cortaFechaLineas.length - 5} más</li>}
            </ul>
          </div>
        </div>
      )}
      {/* Header sticky */}
      <div className="sticky top-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-[#f7f9f8]/95 backdrop-blur border-b border-[#d1dbd6]">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={cancelar} data-testid="invoice-form-back-btn" className="flex-shrink-0">
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5c7368]">Facturación SRI / Facturas</div>
            <h1 className="text-lg md:text-2xl font-bold tracking-tight text-[#15241d] truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
              <FileText className="inline mr-2 -mt-0.5" size={20} />
              {editingId ? (
                <>
                  Factura
                  {form.numero ? (
                    <span className="ml-2 text-[#205c45] font-mono" data-testid="invoice-form-numero">N° {form.numero}</span>
                  ) : null}
                  {form.estado_sri ? (
                    <span className="ml-2 text-xs uppercase tracking-wider text-[#5c7368] font-semibold align-middle">· {form.estado_sri}</span>
                  ) : null}
                </>
              ) : (
                <>
                  Nueva factura electrónica
                  {seqStatus && (
                    <span
                      className="ml-2 text-[#205c45] font-mono text-base align-middle inline-flex items-center gap-1"
                      data-testid="invoice-form-numero-proximo"
                      title="Número que se asignará al guardar"
                    >
                      · N° {lock?.numero_formateado || seqStatus.siguiente_numero}
                      {lock && <Lock size={13} className="text-[#205c45]" title="Número reservado para ti" />}
                    </span>
                  )}
                </>
              )}
            </h1>
            {!editingId && seqStatus && (seqStatus.reservas?.length > 0 || !lock) && (
              <div className="text-[11px] text-[#5c7368] flex items-center gap-2 flex-wrap mt-0.5">
                {seqStatus.reservas?.filter(r => !r.es_propio).map(r => (
                  <Badge key={r.numero} variant="outline" className="text-[10px] bg-[#fef7ec] border-[#f5c477] text-[#8a5500]" data-testid={`reserva-otro-${r.numero}`}>
                    <UsersIcon size={10} className="mr-1" /> #{r.numero_formateado} editando: {r.usuario}
                  </Badge>
                ))}
                {!lock && !editingId && (
                  <button
                    type="button"
                    onClick={reservarSecuencial}
                    disabled={lockBusy}
                    className="inline-flex items-center gap-1 text-[11px] text-[#205c45] hover:text-[#1a4a37] underline disabled:opacity-50"
                    data-testid="reservar-secuencial-btn"
                  >
                    <Lock size={11} /> Fijar este número para mí
                  </button>
                )}
                {lock && (
                  <button
                    type="button"
                    onClick={liberarSecuencial}
                    disabled={lockBusy}
                    className="inline-flex items-center gap-1 text-[11px] text-[#b53a25] hover:text-[#8a2a18] underline disabled:opacity-50"
                    data-testid="liberar-secuencial-btn"
                  >
                    <Unlock size={11} /> Liberar reserva
                  </button>
                )}
              </div>
            )}
            {/* ITER187: aviso de números recuperables disponibles */}
            {!editingId && seqStatus?.tiene_recuperables && !lock && (
              <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded bg-[#e9f2ee] text-[#205c45] border border-[#205c45]/30" data-testid="recuperable-badge">
                💡 Hay {seqStatus.recuperables.length} número(s) liberado(s) disponibles para reutilizar. Al reservar, se usará el menor ({seqStatus.menor_recuperable?.numero_formateado}).
              </div>
            )}
          </div>
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" onClick={cancelar} disabled={saving} data-testid="invoice-form-cancel-btn-desktop">Cancelar</Button>
            <Button onClick={() => save()} disabled={saving} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="invoice-form-save-btn-desktop">
              <Save size={16} className="mr-2" />
              {saving ? "Guardando…" : (editingId ? "Guardar cambios" : "Generar factura")}
            </Button>
          </div>
        </div>
      </div>

      {/* Cabecera */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label className="flex items-center justify-between">
            <span>Cliente (aprobado)</span>
            {form.cliente_id && !editingId && (
              <button
                type="button"
                onClick={() => setConsolidateOpen(true)}
                className="text-[11px] text-[#205c45] hover:underline inline-flex items-center gap-1"
                data-testid="invoice-consolidate-btn"
                title="Fusionar múltiples Notas de Pedido pendientes del cliente en esta factura"
              >
                <Plus size={11} /> Consolidar NP
              </button>
            )}
          </Label>
          <ClienteCombobox
            value={form.cliente_id}
            onChange={(v) => {
              const cli = clientes.find(c => c.id === v);
              setForm(f => ({
                ...f,
                cliente_id: v,
                dias_credito: cli && cli.dias_credito != null ? Number(cli.dias_credito) : (f.dias_credito || 0),
              }));
            }}
            clientes={clientes}
            placeholder="Buscar cliente por razón social, RUC o ciudad…"
            testId="invoice-client"
          />
          {notasPedidoIds.length > 1 && (
            <div className="mt-1 text-[11px] text-[#205c45] bg-[#e9f2ee] border border-[#b2d0c2] rounded px-2 py-1 inline-flex items-center gap-1" data-testid="invoice-consolidated-badge">
              <Layers size={11} /> Factura consolidando {notasPedidoIds.length} Notas de Pedido
            </div>
          )}
          {precioDivergenteAviso && (
            <div className="mt-1 text-[11px] text-[#8a5500] bg-[#fef7ec] border border-[#f5c477] rounded px-2 py-1 inline-flex items-center gap-1" data-testid="invoice-price-diverge-warn">
              <AlertTriangle size={11} /> Precios distintos detectados en NPs origen; revisa cada línea antes de guardar.
            </div>
          )}
        </div>
        <div>
          <Label>Fecha</Label>
          <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="invoice-form-fecha" />
        </div>
        <div>
          <Label>Días de crédito</Label>
          <Input
            type="number"
            min={0}
            value={form.dias_credito ?? 0}
            onChange={(e) => setForm({ ...form, dias_credito: e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0) })}
            data-testid="invoice-form-dias-credito"
            placeholder="0"
          />
          <div className="text-[11px] text-[#5c7368] mt-1">
            Vence:{" "}
            <span className="font-mono text-[#205c45]" data-testid="invoice-form-fecha-venc">
              {(() => {
                try {
                  const d = new Date(form.fecha);
                  d.setDate(d.getDate() + Number(form.dias_credito || 0));
                  return d.toISOString().slice(0, 10);
                } catch { return "—"; }
              })()}
            </span>
          </div>
        </div>
        <div className="md:col-span-2">
          <Label>Forma pago (SRI)</Label>
          <Select value={form.forma_pago_sri} onValueChange={(v) => setForm({ ...form, forma_pago_sri: v })}>
            <SelectTrigger data-testid="invoice-form-forma-pago"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="01">01 - Sin sist. financiero</SelectItem>
              <SelectItem value="15">15 - Compensación deudas</SelectItem>
              <SelectItem value="17">17 - Tarjeta de débito</SelectItem>
              <SelectItem value="19">19 - Tarjeta de crédito</SelectItem>
              <SelectItem value="20">20 - Otros con sist. financiero</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <DireccionEntregaSelect
            cliente={clientes.find(c => c.id === form.cliente_id)}
            value={form.direccion_entrega}
            etiqueta={form.direccion_entrega_etiqueta}
            onChange={({ direccion, etiqueta }) => setForm(f => ({ ...f, direccion_entrega: direccion, direccion_entrega_etiqueta: etiqueta }))}
            testId="invoice-direccion"
          />
        </div>
        <div className="md:col-span-4">
          <Label className="flex items-center justify-between">
            <span>Observaciones (aparece en el RIDE)</span>
            {form.observaciones && form.nota_pedido_id && (
              <button type="button" className="text-[10px] text-[#b53a25] hover:underline" onClick={() => setForm(f => ({ ...f, observaciones: "" }))} data-testid="invoice-clear-obs">
                Limpiar
              </button>
            )}
          </Label>
          <textarea
            value={form.observaciones}
            onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
            placeholder="Ej: Entrega en bodega 2, pago 50% anticipo... (opcional)"
            className="w-full min-h-[60px] border border-[#d1dbd6] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#205c45] focus:border-transparent resize-y"
            data-testid="invoice-form-observaciones"
          />
        </div>
      </div>

      {/* Líneas (tabla compacta, scroll horizontal en móvil) */}
      <div className="border border-[#d1dbd6] rounded-md overflow-x-auto">
        <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Líneas</div>
          <Button size="sm" variant="outline" onClick={addLinea} data-testid="invoice-add-line-btn"><Plus size={14} className="mr-1" />Agregar</Button>
        </div>
        <table className="erp-table" style={{ tableLayout: "fixed", width: "100%", minWidth: "880px" }}>
          <thead><tr><th className="w-8"></th><th style={{ width: "260px" }}>Producto</th><th className="w-40">Cant. + Modo</th><th className="w-28">Precio U.</th><th className="w-20">Desc. %</th><th className="w-24">IVA</th><th className="w-28 text-right">Subtotal</th><th className="w-10"></th></tr></thead>
          <tbody>
            {form.lineas.map((l, i) => (
              <React.Fragment key={i}>
                <tr>
                  <td className="text-center">
                    <button type="button" className="text-[#5c7368] hover:text-[#205c45]" onClick={() => toggleExpand(i)} title="Información adicional del producto">
                      {expandedLines[i] ? "▾" : "▸"}
                    </button>
                  </td>
                  <td>
                    <ProductoCombobox value={l.producto_comercial_id} onChange={(v) => onProductoChange(i, v)} productos={prods} testId={`inv-prod-${i}`} />
                    {l.producto_comercial_id && (l.numero_lote || l.laboratorio) && (
                      <div className="mt-1 flex gap-1 flex-wrap text-[10px]">
                        {l.laboratorio && (
                          <span className="px-1.5 py-0.5 rounded bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]">{l.laboratorio}</span>
                        )}
                        {l.numero_lote && (
                          <span className="px-1.5 py-0.5 rounded bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]" title="Lote FEFO sugerido">
                            ⚡ Lote {l.numero_lote}{l.fecha_vencimiento ? ` · Vence ${l.fecha_vencimiento}` : ""}
                          </span>
                        )}
                        {l._margen_fuente && l._margen_aplicado > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-[#eef2f9] text-[#2d4a73] border border-[#b2c5db]" title="Margen aplicado">
                            {(l._margen_aplicado * 100).toFixed(0)}% · {l._margen_fuente === "producto" ? "producto" : l._margen_fuente === "tipo_cliente" ? "tipo cliente" : "default"}
                          </span>
                        )}
                        {l._lotes_disponibles?.length > 1 && (
                          <button type="button" onClick={() => toggleExpand(i)} className="px-1.5 py-0.5 rounded bg-white text-[#5c7368] border border-[#d1dbd6] hover:bg-[#f7f9f8]">
                            +{l._lotes_disponibles.length - 1} lote(s) disponibles · cambiar
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1 items-center">
                      <NumericInput className="h-9 text-right tabular-nums w-20" value={l.cantidad} onChange={(e) => updateLinea(i, "cantidad", parseFloat(e.target.value) || 0)} data-testid={`cant-${i}`} />
                      {l._unidades_por_caja > 1 ? (
                        <div className="flex gap-0.5 text-[10px] flex-shrink-0">
                          <button type="button" onClick={() => toggleUnidadVenta(i, "unidad")} className={`px-1.5 py-1 rounded border ${l._unidad_venta === "unidad" ? "bg-[#205c45] text-white border-[#205c45]" : "bg-white text-[#5c7368] border-[#d1dbd6]"}`} title="Unidad" data-testid={`unidad-btn-${i}`}>U</button>
                          <button type="button" onClick={() => toggleUnidadVenta(i, "caja")} className={`px-1.5 py-1 rounded border ${l._unidad_venta === "caja" ? "bg-[#205c45] text-white border-[#205c45]" : "bg-white text-[#5c7368] border-[#d1dbd6]"}`} title="Caja" data-testid={`caja-btn-${i}`}>C</button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-[#8ba397]">U</span>
                      )}
                    </div>
                    {(l._unidad_venta === "caja" && l._unidades_por_caja > 1) ? (
                      <div className="text-[11px] text-white font-semibold mt-1 px-1.5 py-0.5 rounded bg-[#205c45] inline-block" data-testid={`presentacion-badge-${i}`}>
                        📦 {l._unidades_por_caja} u/caja
                      </div>
                    ) : (
                      <div className="text-[11px] text-[#5c7368] mt-1 px-1.5 py-0.5 rounded bg-[#eef3f1] border border-[#d1dbd6] inline-block">
                        ▢ por {l.presentacion ? l.presentacion.split(/\s+/).slice(-1)[0].toLowerCase() : "unidad"}
                      </div>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const isCaja = l._unidad_venta === "caja" && l._unidades_por_caja > 1;
                      const opts = isCaja ? {
                        precio_fijo: l._pvp_caja,
                        precio_margen: l._precio_caja,
                        precio_minimo: l._precio_minimo_caja,
                      } : {
                        precio_fijo: l._pvp_unidad,
                        precio_margen: l._precio_unidad,
                        precio_minimo: l._precio_minimo_unidad,
                      };
                      const piso = isCaja ? l._precio_minimo_caja : l._precio_minimo_unidad;
                      const belowMin = piso && +l.precio_unitario > 0 && +l.precio_unitario + 1e-6 < +piso;
                      return (
                        <PriceCombobox
                          value={l.precio_unitario}
                          onChange={(v) => updateLinea(i, "precio_unitario", v)}
                          options={opts}
                          decimals={7}
                          belowMin={belowMin}
                          testId={`precio-${i}`}
                        />
                      );
                    })()}
                    {(() => {
                      // ITER211: margen SIEMPRE visible y con conversión correcta
                      // por caja (antes solo se mostraba la alerta y en modo caja
                      // el cálculo usaba el precio de caja contra el costo unitario).
                      const costo = parseFloat(l._costo_unidad) || 0;
                      const precio = parseFloat(l.precio_unitario) || 0;
                      const margenAplicado = parseFloat(l._margen_aplicado) || 0;
                      if (!precio) return null;
                      if (!costo) {
                        if (!verCostos) return null;
                        return (
                          <div
                            className="mt-0.5 text-[10px] font-semibold rounded px-1 py-0.5 border flex items-center gap-1 bg-[#f7f9f8] text-[#5c7368] border-[#d1dbd6]"
                            title="No se puede calcular el margen: el lote/producto no tiene costo registrado."
                            data-testid={`margen-sin-costo-${i}`}
                          >
                            <AlertTriangle size={10} />
                            <span>Sin costo · margen n/d</span>
                          </div>
                        );
                      }
                      const esCaja = l._unidad_venta === "caja" && l._unidades_por_caja > 1;
                      const precioUnid = esCaja ? precio / l._unidades_por_caja : precio;
                      const margenReal = 1 - (costo / precioUnid);
                      const bajo = !!margenAplicado && (margenReal - margenAplicado) < -0.005;
                      const isNegativo = margenReal < 0;
                      // Quien no puede ver costos solo ve la ALERTA, no el margen informativo
                      if (!verCostos && !bajo && !isNegativo) return null;
                      return (
                        <div
                          className={`mt-0.5 text-[10px] font-semibold rounded px-1 py-0.5 border flex items-center gap-1 ${
                            isNegativo ? "bg-[#fdecea] text-[#b53a25] border-[#d99584]"
                            : bajo ? "bg-[#fef7ec] text-[#8a5500] border-[#f5c477]"
                            : "bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]"}`}
                          title={`Margen real ${(margenReal * 100).toFixed(1)}%${margenAplicado ? ` · configurado ${(margenAplicado * 100).toFixed(0)}%` : ""}${isNegativo ? " (PRECIO DEBAJO DEL COSTO)" : ""} · costo unidad $${costo.toFixed(4)}`}
                          data-testid={`margen-${i}`}
                        >
                          {(isNegativo || bajo) && <AlertTriangle size={10} />}
                          <span className="tabular-nums">
                            Margen {(margenReal * 100).toFixed(1)}%
                            {bajo && ` < ${(margenAplicado * 100).toFixed(0)}%`}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    <div className="relative">
                      <NumericInput className="h-9 text-right tabular-nums w-full pr-6 no-spinner" min="0" max="100" step="0.5" value={l.descuento_pct ?? 0} onChange={(e) => updateLinea(i, "descuento_pct", parseFloat(e.target.value) || 0)} />
                      <span className="absolute right-1.5 top-2 text-[10px] text-[#5c7368] font-semibold pointer-events-none">%</span>
                    </div>
                  </td>
                  <td>
                    <Select value={String(l.iva_porc)} onValueChange={(v) => updateLinea(i, "iva_porc", parseFloat(v))}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="-1">Exento</SelectItem>
                        <SelectItem value="0">0%</SelectItem>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="12">12%</SelectItem>
                        <SelectItem value="15">15%</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="text-right font-semibold tabular-nums">${l.subtotal.toFixed(2)}</td>
                  <td><Button size="icon" variant="ghost" onClick={() => setForm(f => ({ ...f, lineas: f.lineas.filter((_, x) => x !== i) }))}><Trash2 size={14} /></Button></td>
                </tr>
                {expandedLines[i] && (
                  <tr className="bg-[#f7f9f8]">
                    <td></td>
                    <td colSpan={7} className="py-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Marca</Label><Input className="h-8 text-xs" value={l.marca || ""} onChange={(e) => updateLinea(i, "marca", e.target.value)} /></div>
                        <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Laboratorio</Label><Input className="h-8 text-xs" value={l.laboratorio || ""} onChange={(e) => updateLinea(i, "laboratorio", e.target.value)} /></div>
                        <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Presentación</Label><Input className="h-8 text-xs" value={l.presentacion || ""} onChange={(e) => updateLinea(i, "presentacion", e.target.value)} /></div>
                        <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Registro sanitario</Label><Input className="h-8 text-xs" value={l.registro_sanitario || ""} onChange={(e) => updateLinea(i, "registro_sanitario", e.target.value)} /></div>
                        {l._lotes_disponibles && l._lotes_disponibles.length > 0 ? (
                          <div className="col-span-2" data-testid={`lote-selector-${i}`}>
                            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Lote a despachar · Override FEFO</Label>
                            <Select value={l.lote_id || ""} onValueChange={(v) => onLoteChange(i, v)}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleccionar lote..." /></SelectTrigger>
                              <SelectContent>
                                {l._lotes_disponibles.map((lt, idx) => {
                                  const isFefo = idx === 0;
                                  return (
                                    <SelectItem key={lt.id} value={lt.id}>
                                      {lt.numero_lote} · Vence {lt.fecha_vencimiento} · Stock {lt.cantidad_disponible}
                                      {isFefo ? " ⚡ FEFO" : ""}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">N° Lote</Label><Input className="h-8 text-xs" value={l.numero_lote || ""} onChange={(e) => updateLinea(i, "numero_lote", e.target.value)} /></div>
                        )}
                        <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Fecha elaboración</Label><Input className="h-8 text-xs" type="date" value={l.fecha_elaboracion || ""} onChange={(e) => updateLinea(i, "fecha_elaboracion", e.target.value)} /></div>
                        <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Fecha vencimiento</Label><Input className="h-8 text-xs" type="date" value={l.fecha_vencimiento || ""} onChange={(e) => updateLinea(i, "fecha_vencimiento", e.target.value)} /></div>
                        <div className="col-span-2 md:col-span-4"><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Observación / nota adicional</Label><Input className="h-8 text-xs" value={l.observacion || ""} onChange={(e) => updateLinea(i, "observacion", e.target.value)} placeholder="Ej: Producto sin refrigeración especial, cliente confirmó entrega en 2 cuotas..." /></div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {form.lineas.length > 0 && (
              <tr>
                <td colSpan={8} className="p-2 bg-[#f7fbf9] border-t border-[#d1dbd6]">
                  <Button size="sm" variant="outline" onClick={addLinea} className="w-full" data-testid="invoice-add-line-btn-bottom">
                    <Plus size={14} className="mr-1" />Agregar producto
                  </Button>
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-[#f7f9f8] border-t">
            <tr><td colSpan={6} className="text-right font-semibold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-1">Subtotal</td><td colSpan={2} className="text-right tabular-nums px-3 py-1">${totales.subtotal.toFixed(2)}</td></tr>
            <tr><td colSpan={6} className="text-right font-semibold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-1">IVA</td><td colSpan={2} className="text-right tabular-nums px-3 py-1">${totales.iva.toFixed(2)}</td></tr>
            <tr><td colSpan={6} className="text-right font-bold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-2">Total</td><td colSpan={2} className="text-right font-bold text-lg tabular-nums px-3 py-2">${totales.total.toFixed(2)}</td></tr>
          </tfoot>
        </table>
      </div>

      {resumenCaja.lineasCaja > 0 && (
        <div className="bg-[#e9f2ee] border border-[#b2d0c2] rounded-md px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid="caja-summary-bar">
          <span className="text-[#205c45] font-semibold uppercase text-xs tracking-wider">📦 Resumen por caja</span>
          <span><b>{resumenCaja.lineasCaja}</b> líneas en caja</span>
          <span><b>{resumenCaja.totalCajas}</b> cajas totales</span>
          <span>Equivale a <b>{resumenCaja.totalUnidEquiv.toLocaleString()}</b> unidades</span>
          {resumenCaja.totalUnidades > 0 && <span className="text-[#5c7368]">+ {resumenCaja.totalUnidades} unidades sueltas</span>}
        </div>
      )}

      {/* Información Adicional */}
      <div className="border border-[#d1dbd6] rounded-md" data-testid="info-adicional-block">
        <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Información Adicional · aparece en el RIDE/PDF</div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setForm(f => ({ ...f, informacion_adicional: [...(f.informacion_adicional || []), { label: "", valor: "" }] }))}
            data-testid="info-adicional-add"
          >
            <Plus size={14} className="mr-1" /> Agregar campo
          </Button>
        </div>
        <div className="p-3 space-y-2">
          {(form.informacion_adicional || []).length === 0 && (
            <div className="text-xs text-[#8ba397] italic">Sin campos de información adicional. Pulse &quot;Agregar campo&quot;.</div>
          )}
          {(form.informacion_adicional || []).map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <Input
                className="h-8 text-xs col-span-4 md:col-span-3"
                placeholder="Etiqueta (ej: Email)"
                value={it.label}
                onChange={(e) => setForm(f => {
                  const ia = [...(f.informacion_adicional || [])];
                  ia[i] = { ...ia[i], label: e.target.value };
                  return { ...f, informacion_adicional: ia };
                })}
                data-testid={`info-adicional-label-${i}`}
              />
              <Input
                className="h-8 text-xs col-span-7 md:col-span-8"
                placeholder="Valor"
                value={it.valor}
                onChange={(e) => setForm(f => {
                  const ia = [...(f.informacion_adicional || [])];
                  ia[i] = { ...ia[i], valor: e.target.value };
                  return { ...f, informacion_adicional: ia };
                })}
                data-testid={`info-adicional-valor-${i}`}
              />
              <Button
                size="icon"
                variant="ghost"
                className="col-span-1"
                onClick={() => setForm(f => ({ ...f, informacion_adicional: (f.informacion_adicional || []).filter((_, x) => x !== i) }))}
                data-testid={`info-adicional-remove-${i}`}
              >
                <Trash2 size={14} className="text-[#b53a25]" />
              </Button>
            </div>
          ))}
          <p className="text-[10px] text-[#5c7368] italic">Use &quot;Agregar campo&quot; para incluir datos como Email, Dirección, Orden de compra u otros que aparecerán en el RIDE/PDF.</p>
        </div>
      </div>

      {/* Barra fija inferior móvil — bottom-[64px] deja espacio al badge "Made with Emergent" en preview */}
      <div className="fixed bottom-[64px] left-0 right-0 z-[60] border-t border-[#d1dbd6] bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.08)] md:hidden">
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <div className="flex flex-col text-right pr-1 leading-tight">
            <span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Total</span>
            <span className="text-lg font-bold tabular-nums text-[#15241d]">${totales.total.toFixed(2)}</span>
          </div>
          <div className="flex gap-2 flex-1 justify-end">
            <Button variant="outline" className="h-11 flex-1 max-w-[110px]" onClick={cancelar} disabled={saving} data-testid="invoice-form-cancel-btn">Cancelar</Button>
            <Button onClick={() => save()} disabled={saving} className="bg-[#205c45] hover:bg-[#1a4a37] h-11 flex-1" data-testid="invoice-form-save-btn">
              <Save size={16} className="mr-2" />
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </div>
      </div>

      <PinGerenciaModal
        open={!!pinModal}
        violaciones={pinModal?.violaciones || []}
        onClose={() => setPinModal(null)}
        onAuthorized={(autoriz) => { setPinModal(null); save(autoriz); }}
      />

      <AutoSplitFEFODialog
        open={!!splitProposal}
        split={splitProposal?.split}
        productoNombre={splitProposal?.producto_nombre}
        onConfirm={applyProposedSplit}
        onCancel={() => setSplitProposal(null)}
      />

      <ConsolidateOrdersDialog
        open={consolidateOpen}
        onClose={() => setConsolidateOpen(false)}
        clienteId={form.cliente_id}
        excluirId={form.nota_pedido_id || null}
        kind="pedido"
        onConsolidated={applyConsolidation}
      />
    </div>
  );
}
